import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { experimental_useObject as useObject } from "@ai-sdk/react";
import { z } from "zod";
import { api, API_BASE } from "@/lib/api-client";
import type { OpportunityReviewRequest } from "@/types";

// ---------------------------------------------------------------------------
// Query key factory – keeps keys consistent and simplifies invalidation
// ---------------------------------------------------------------------------

export const queryKeys = {
  dashboard: {
    metrics: ["dashboard", "metrics"] as const,
    cases: (limit: number) => ["dashboard", "cases", limit] as const,
    limitationAlerts: ["dashboard", "limitationAlerts"] as const,
    intelligenceFeed: (limit: number) =>
      ["dashboard", "intelligence-feed", limit] as const,
    trendPills: ["dashboard", "trend-pills"] as const,
  },
  cases: {
    all: ["cases"] as const,
    list: (params: Record<string, string | number | undefined>) =>
      ["cases", params] as const,
    detail: (id: string) => ["cases", id] as const,
    gate: (caseId: string) => ["cases", caseId, "gate"] as const,
    evidence: (caseId: string) => ["cases", caseId, "evidence"] as const,
    memos: (caseId: string) => ["cases", caseId, "memos"] as const,
    memoDetail: (caseId: string, memoId: string) =>
      ["cases", caseId, "memos", memoId] as const,
    timeline: (caseId: string) => ["cases", caseId, "timeline"] as const,
  },
  discovery: {
    facets: ["discovery", "facets"] as const,
    opportunities: (params: Record<string, string | number | undefined>) =>
      ["discovery", "opportunities", params] as const,
    grouped: (params: Record<string, string | number | undefined>) =>
      ["discovery", "grouped", params] as const,
    feed: (params: Record<string, string | number | boolean | undefined>) =>
      ["discovery", "feed", params] as const,
    group: (oppId: string) => ["discovery", "group", oppId] as const,
    detail: (oppId: string) => ["discovery", "detail", oppId] as const,
    precedents: (oppId: string) => ["discovery", "precedents", oppId] as const,
    similarIncidents: (oppId: string) =>
      ["discovery", "similar-incidents", oppId] as const,
    entityProfile: (oppId: string) =>
      ["discovery", "entity-profile", oppId] as const,
    webPrecedents: (oppId: string) =>
      ["discovery", "web-precedents", oppId] as const,
    enrichment: (oppId: string) =>
      ["discovery", "enrichment", oppId] as const,
    chronology: (oppId: string) =>
      ["discovery", "chronology", oppId] as const,
    agents: (oppId: string) => ["discovery", "agents", oppId] as const,
  },
  connectors: {
    freshness: ["connectors", "freshness"] as const,
    health: ["connectors", "health"] as const,
  },
  violations: {
    all: ["violations"] as const,
  },
  analysis: {
    list: (caseId: string) => ["analysis", caseId] as const,
  },
  jobs: {
    detail: (jobId: string) => ["jobs", jobId] as const,
  },
  savedSearches: {
    all: ["saved-searches"] as const,
  },
} as const;

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export function useDashboardMetrics() {
  return useQuery({
    queryKey: queryKeys.dashboard.metrics,
    queryFn: () => api.getMetrics(),
  });
}

export function useDashboardCases(limit = 10) {
  return useQuery({
    queryKey: queryKeys.dashboard.cases(limit),
    queryFn: () => api.getDashboardCases(limit),
  });
}

export function useLimitationAlerts() {
  return useQuery({
    queryKey: queryKeys.dashboard.limitationAlerts,
    queryFn: () => api.getLimitationAlerts(),
  });
}

export function useIntelligenceFeed(limit = 10) {
  return useQuery({
    queryKey: queryKeys.dashboard.intelligenceFeed(limit),
    queryFn: () => api.getIntelligenceFeed(limit),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

// LEG-101 Eng-4: trend-pills cache reader. Refetches every 30 minutes —
// the daily backend job sets fresh pills with a 25h TTL, so polling more
// often than that just churns. Returns whatever's in Redis right now.
export function useTrendPills() {
  return useQuery({
    queryKey: queryKeys.dashboard.trendPills,
    queryFn: () => api.getTrendPills(),
    staleTime: 30 * 60 * 1000,
    refetchInterval: 30 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Cases
// ---------------------------------------------------------------------------

export function useCases(
  params: Record<string, string | number | undefined> = {}
) {
  return useQuery({
    queryKey: queryKeys.cases.list(params),
    queryFn: () => api.listCases(params),
  });
}

export function useCase(id: string) {
  return useQuery({
    queryKey: queryKeys.cases.detail(id),
    queryFn: () => api.getCase(id),
    enabled: !!id,
  });
}

export function useCreateCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createCase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboard.metrics,
      });
    },
  });
}

export function useUpdateCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateCase(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.all });
    },
  });
}

export function useAdvanceStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      targetStage,
      expectedUpdatedAt,
    }: {
      caseId: string;
      targetStage: string;
      // Eng-6: pass the case's updated_at the client read. On mismatch,
      // backend returns 409 ConcurrencyError and the DnD layer reverts.
      expectedUpdatedAt?: string;
    }) => api.advanceStage(caseId, targetStage, expectedUpdatedAt),
    onSuccess: (_data, { caseId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.detail(caseId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.gate(caseId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.timeline(caseId),
      });
    },
  });
}

/**
 * Generate an AI Briefing for a single opportunity. On success, invalidates
 * the grouped feed so the card re-renders with the colored Legal Basis /
 * Key Actions / Key Risks zones populated.
 *
 * 2026-05-20 (/plan-eng-review T1d): pass `force: true` so the backend
 * bypasses the already-enriched guard. Without this, the "Refresh briefing"
 * button silently returned `skipped_already_enriched` and the stale prose
 * stayed. The button's whole purpose is to re-run, so force is always on.
 */
export function useGenerateBriefing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (oppId: string) => api.generateBriefing(oppId, { force: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["discovery", "grouped"] });
    },
  });
}

export function useGateStatus(caseId: string) {
  return useQuery({
    queryKey: queryKeys.cases.gate(caseId),
    queryFn: () => api.getGateStatus(caseId),
    enabled: !!caseId,
  });
}

export function useCaseEvidence(caseId: string) {
  return useQuery({
    queryKey: queryKeys.cases.evidence(caseId),
    queryFn: () => api.getCaseEvidence(caseId),
    enabled: !!caseId,
  });
}

export function useCaseMemos(caseId: string) {
  return useQuery({
    queryKey: queryKeys.cases.memos(caseId),
    queryFn: () => api.getCaseMemos(caseId),
    enabled: !!caseId,
  });
}

export function useMemoDetail(caseId: string, memoId: string | null) {
  return useQuery({
    queryKey: queryKeys.cases.memoDetail(caseId, memoId ?? ""),
    queryFn: () => api.getMemoDetail(caseId, memoId!),
    enabled: !!caseId && !!memoId,
  });
}

export function useCaseTimeline(caseId: string) {
  return useQuery({
    queryKey: queryKeys.cases.timeline(caseId),
    queryFn: () => api.getCaseTimeline(caseId),
    enabled: !!caseId,
  });
}

// ---------------------------------------------------------------------------
// Discovery
// ---------------------------------------------------------------------------

export function useFacets() {
  return useQuery({
    queryKey: queryKeys.discovery.facets,
    queryFn: () => api.listFacets(),
  });
}

export function useOpportunities(
  params: Record<string, string | number | undefined> = {}
) {
  return useQuery({
    queryKey: queryKeys.discovery.opportunities(params),
    queryFn: () => api.listOpportunities(params),
  });
}

export function useGroupedOpportunities(
  params: Record<string, string | number | undefined> = {},
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.discovery.grouped(params),
    queryFn: () => api.listGroupedOpportunities(params),
    // plan-eng-review A4 2026-05-03: callers gate on AuthContext
    // isHydrated so the first /grouped fetch after onboarding doesn't
    // fire with an empty firmClaimTypes filter and return zero rows.
    // Default `enabled: true` preserves existing behaviour for callers
    // that don't pass `enabled`.
    enabled: opts.enabled ?? true,
  });
}

/**
 * P1c — indexed early-signal feed. Server does practice-area / tier / chip
 * filtering + SQL chip counts; replaces useGroupedOpportunities + the
 * client-side feed-adapter on /discovery.
 */
export function useFeed(
  params: Record<string, string | number | boolean | undefined> = {},
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.discovery.feed(params),
    queryFn: () => api.getFeed(params),
    enabled: opts.enabled ?? true,
  });
}

export function usePromoteOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (oppId: string) => api.promoteOpportunity(oppId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["discovery", "opportunities"],
      });
      queryClient.invalidateQueries({ queryKey: ["discovery", "grouped"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.cases.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboard.metrics,
      });
      queryClient.invalidateQueries({
        queryKey: ["dashboard", "intelligence-feed"],
      });
    },
  });
}

export function useOpportunityGroup(oppId: string) {
  return useQuery({
    queryKey: queryKeys.discovery.group(oppId),
    queryFn: () => api.getOpportunityGroup(oppId),
    enabled: !!oppId,
  });
}

/**
 * P1c — opp-detail DTO (verdict band + Value/Readiness axes) from the indexed
 * /detail endpoint. The group hook still serves the other tabs (Evidence,
 * signals); this drives the decision header + Overview axes (A2).
 */
export function useOppDetail(oppId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.discovery.detail(oppId),
    queryFn: () => api.getOppDetail(oppId),
    enabled: !!oppId && enabled,
  });
}

/**
 * Plan C — chronology timeline for the Overview tab. Pure projection on
 * the same signal-group endpoint, so cache TTL matches the group hook.
 */
export function useOpportunityChronology(oppId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.discovery.chronology(oppId),
    queryFn: () => api.getOpportunityChronology(oppId),
    enabled: !!oppId && enabled,
  });
}

export function usePrecedents(oppId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.discovery.precedents(oppId),
    queryFn: () => api.getOpportunityPrecedents(oppId),
    enabled: !!oppId && enabled,
  });
}

export function useSearchPrecedents(oppId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.searchPrecedents(oppId),
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKeys.discovery.webPrecedents(oppId),
        data,
      );
      // Re-fetch the merged precedents list (now includes web results)
      queryClient.invalidateQueries({
        queryKey: queryKeys.discovery.precedents(oppId),
      });
    },
  });
}

export function useSimilarIncidents(oppId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.discovery.similarIncidents(oppId),
    queryFn: () => api.getSimilarIncidents(oppId),
    enabled: !!oppId && enabled,
  });
}

export function useEntityProfile(oppId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.discovery.entityProfile(oppId),
    queryFn: () => api.getEntityProfile(oppId),
    enabled: !!oppId && enabled,
  });
}

export function useEntityEnrichment(oppId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.discovery.enrichment(oppId),
    queryFn: () => api.getEntityEnrichment(oppId),
    enabled: !!oppId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useEnrichOpportunity(oppId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.enrichOpportunity(oppId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.discovery.enrichment(oppId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.discovery.group(oppId),
      });
    },
  });
}

/* ─── Part B — adversarial agents (Investigator → Defendant Counsel → Judge) ─── */

/** Read the current agent runs (Defence Memo + Judge verdict + sign-off state). */
export function useAgents(oppId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.discovery.agents(oppId),
    queryFn: () => api.getAgents(oppId),
    enabled: !!oppId && enabled,
  });
}

/** Run the adversarial pipeline on-demand; seeds the agents cache with the result. */
export function useRunAgents(oppId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.runAgents(oppId),
    onSuccess: (data) => {
      // The run endpoint returns the same shape as the read endpoint, so write
      // it straight into the cache — no extra round-trip to render the panel.
      queryClient.setQueryData(queryKeys.discovery.agents(oppId), data);
    },
  });
}

/** Counsel/admin sign-off — flips the Judge verdict draft → signed. */
export function useSignoffAgents(oppId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.signoffAgents(oppId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.discovery.agents(oppId),
      });
    },
  });
}

// Zod schema for streaming deep analysis (mirrors backend DeepAnalysisSchema)
const deepAnalysisSchema = z.object({
  viability_summary: z.string(),
  evidence_summary: z.string(),
  risk_factors: z.array(z.string()),
  recommended_steps: z.array(z.object({ title: z.string(), detail: z.string() })),
  limitation_notes: z.string().nullable(),
  estimated_claimants: z.string().nullable(),
  confidence_explanation: z.string(),
});

export const STREAM_API_BASE = API_BASE;
const STREAM_TEST_AUTH = process.env.NEXT_PUBLIC_TEST_AUTH;
// FINDING-001: when Cognito is configured, the backend rejects test
// tokens with "Test tokens are not accepted in production". We must
// PREFER the cached Cognito JWT and skip the test-token fallback even
// if NEXT_PUBLIC_TEST_AUTH is set in the environment (it's set in the
// dev compose file but unsafe whenever the backend runs cognito mode).
const STREAM_HAS_COGNITO = !!process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID;

let _cachedStreamToken: string | null = null;

export function getStreamAuthHeader(): string {
  if (_cachedStreamToken) return _cachedStreamToken;
  if (STREAM_HAS_COGNITO) return ""; // wait for the AuthContext to populate
  if (STREAM_TEST_AUTH) return STREAM_TEST_AUTH;
  if (process.env.NODE_ENV === "development") {
    return "Test 10000000-0000-0000-0000-000000000001:analyst:00000000-0000-0000-0000-000000000001";
  }
  return "";
}

export function setStreamAuthToken(token: string) {
  _cachedStreamToken = `Bearer ${token}`;
}

export function clearStreamAuthToken() {
  _cachedStreamToken = null;
}

// ── Analysis streaming schemas (mirror backend models/analysis.ts) ──

export const certificationStreamSchema = z.object({
  likely_certification: z.string(),
  confidence_pct: z.number(),
  strengths: z.array(z.string()),
  risks: z.array(z.string()),
  recommendation: z.string(),
});

export const settlementStreamSchema = z.object({
  expected_settlement_range_gbp: z.string(),
  settlement_probability_pct: z.number(),
  drivers: z.array(z.string()),
  risks: z.array(z.string()),
  recommendation: z.string(),
});

export const durationCostsStreamSchema = z.object({
  expected_duration_months: z.number(),
  expected_cost_range_gbp: z.string(),
  cost_drivers: z.array(z.string()),
  risks: z.array(z.string()),
  recommendation: z.string(),
});

export const proceduralRouteStreamSchema = z.object({
  recommended_route: z.string(),
  confidence_pct: z.number(),
  reasoning: z.string(),
  alternative_routes: z.array(z.object({
    route: z.string(),
    pros: z.array(z.string()),
    cons: z.array(z.string()),
  })),
  key_precedents: z.array(z.string()),
  recommendation: z.string(),
});

export function useStreamAnalysis(oppId: string) {
  const queryClient = useQueryClient();

  return useObject({
    api: `${STREAM_API_BASE}/api/discovery/opportunities/${oppId}/analyze`,
    schema: deepAnalysisSchema,
    headers: {
      Authorization: getStreamAuthHeader(),
    },
    onFinish() {
      queryClient.invalidateQueries({
        queryKey: queryKeys.discovery.group(oppId),
      });
      queryClient.invalidateQueries({
        queryKey: ["discovery", "opportunities"],
      });
      queryClient.invalidateQueries({ queryKey: ["discovery", "grouped"] });
    },
  });
}

export function useReviewOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      oppId,
      body,
    }: {
      oppId: string;
      body: OpportunityReviewRequest;
    }) => api.reviewOpportunity(oppId, body),
    onSuccess: (_data, { oppId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.discovery.group(oppId),
      });
      queryClient.invalidateQueries({
        queryKey: ["discovery", "opportunities"],
      });
      queryClient.invalidateQueries({ queryKey: ["discovery", "grouped"] });
      queryClient.invalidateQueries({
        queryKey: ["dashboard", "intelligence-feed"],
      });
    },
  });
}

export function useTriggerScan() {
  return useMutation({
    mutationFn: (filters: Record<string, unknown>) => api.triggerScan(filters),
  });
}

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------

export function useRunAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type, caseId }: { type: string; caseId: string }) =>
      api.runAnalysis(type, caseId),
    onSuccess: (_data, { caseId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.analysis.list(caseId),
      });
    },
  });
}

export function useAnalyses(caseId: string) {
  return useQuery({
    queryKey: queryKeys.analysis.list(caseId),
    queryFn: () => api.getAnalyses(caseId),
    enabled: !!caseId,
  });
}

// ---------------------------------------------------------------------------
// Research & Memo
// ---------------------------------------------------------------------------

export function useTriggerResearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => api.triggerResearch(caseId),
    onSuccess: (_data, caseId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.evidence(caseId),
      });
    },
  });
}

export function useTriggerMemo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => api.triggerMemo(caseId),
    onSuccess: (_data, caseId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.memos(caseId),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Calculator
// ---------------------------------------------------------------------------

export function useComputeProfit() {
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.computeProfit(data),
  });
}

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

export function useJob(
  jobId: string | null,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: queryKeys.jobs.detail(jobId ?? ""),
    queryFn: () => api.getJob(jobId!),
    enabled: !!jobId && (options.enabled ?? true),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (
        status === "completed" ||
        status === "failed" ||
        status === "cancelled"
      ) {
        return false;
      }
      return 3000;
    },
    // Keep polling while the tab is backgrounded. Scans run for minutes; users
    // switch tabs and come back — without this the poll pauses on blur and the
    // completion edge (which drives the toast + feed refetch) can be missed.
    refetchIntervalInBackground: true,
  });
}

export function useCancelJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.cancelJob(jobId),
    onSuccess: (_data, jobId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.jobs.detail(jobId),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Connector freshness
// ---------------------------------------------------------------------------

export function useConnectorFreshness() {
  return useQuery({
    queryKey: queryKeys.connectors.freshness,
    queryFn: () => api.getConnectorFreshness(),
    staleTime: 60_000, // refresh every minute
    refetchInterval: 60_000,
  });
}

export function useConnectorHealth() {
  return useQuery({
    queryKey: queryKeys.connectors.health,
    queryFn: () => api.getConnectorHealth(),
    staleTime: 30_000,
    refetchInterval: 60_000,
    // Keep last known state while refetching — avoids flicker to "unknown"
    placeholderData: (prev) => prev,
  });
}

// ---------------------------------------------------------------------------
// Violation Scanner
// ---------------------------------------------------------------------------

export function useScanViolations() {
  return useMutation({
    mutationFn: (data: { url?: string; text?: string }) =>
      api.scanViolations(data),
  });
}

export function useScanHistory() {
  return useQuery({
    queryKey: ["violation-scans"],
    queryFn: () => api.getScanHistory(),
  });
}

export function useScanPoll(scanId: string | null) {
  return useQuery({
    queryKey: ["violation-scan", scanId],
    queryFn: () => api.getScan(scanId!),
    enabled: !!scanId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "completed" || status === "failed") return false;
      return 3000; // poll every 3s while pending/running
    },
  });
}

// ---------------------------------------------------------------------------
// Saved Searches (LEG-98 P5)
// ---------------------------------------------------------------------------

export function useSavedSearches() {
  return useQuery({
    queryKey: queryKeys.savedSearches.all,
    queryFn: () => api.listSavedSearches(),
  });
}

export function useCreateSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      filter_params: Record<string, unknown>;
    }) => api.createSavedSearch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.savedSearches.all });
    },
  });
}

export function useUpdateSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      filter_params?: Record<string, unknown>;
      enabled?: boolean;
    }) => api.updateSavedSearch(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.savedSearches.all });
    },
  });
}

export function useDeleteSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteSavedSearch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.savedSearches.all });
    },
  });
}
